/**
 * Accessibility Utilities
 * Helper functions and hooks for improving accessibility (a11y)
 */

import { useEffect, useRef, useCallback } from 'react';

// ==========================================
// FOCUS MANAGEMENT
// ==========================================

/**
 * Hook to trap focus within a container (useful for modals)
 */
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus first element when trap is activated
    firstElement?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  return containerRef;
}

/**
 * Hook to restore focus when component unmounts
 */
export function useRestoreFocus() {
  const previousElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousElement.current = document.activeElement as HTMLElement;

    return () => {
      previousElement.current?.focus();
    };
  }, []);
}

/**
 * Hook to manage focus for lists (arrow key navigation)
 */
export function useArrowKeyNavigation<T extends HTMLElement>(
  itemCount: number,
  options: {
    orientation?: 'vertical' | 'horizontal';
    loop?: boolean;
  } = {}
) {
  const { orientation = 'vertical', loop = true } = options;
  const containerRef = useRef<T>(null);
  const currentIndex = useRef(0);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isVertical = orientation === 'vertical';
      const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
      const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';

      if (event.key !== prevKey && event.key !== nextKey) return;

      event.preventDefault();

      const container = containerRef.current;
      if (!container) return;

      const items = container.querySelectorAll<HTMLElement>('[role="option"], [role="menuitem"], button, a');
      if (items.length === 0) return;

      if (event.key === prevKey) {
        currentIndex.current = loop
          ? (currentIndex.current - 1 + items.length) % items.length
          : Math.max(0, currentIndex.current - 1);
      } else {
        currentIndex.current = loop
          ? (currentIndex.current + 1) % items.length
          : Math.min(items.length - 1, currentIndex.current + 1);
      }

      items[currentIndex.current]?.focus();
    },
    [orientation, loop, itemCount]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return containerRef;
}

// ==========================================
// SCREEN READER UTILITIES
// ==========================================

/**
 * Announce message to screen readers
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
) {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement is read
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Hook for live region announcements
 */
export function useLiveAnnouncer() {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    announceToScreenReader(message, priority);
  }, []);

  return { announce };
}

// ==========================================
// KEYBOARD UTILITIES
// ==========================================

/**
 * Hook to handle Escape key press
 */
export function useEscapeKey(handler: () => void, isActive = true) {
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handler();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handler, isActive]);
}

/**
 * Hook to handle Enter/Space key for custom interactive elements
 */
export function useActivateOnEnterSpace<T extends HTMLElement>(handler: () => void) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handler();
      }
    };

    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  }, [handler]);

  return ref;
}

// ==========================================
// ARIA HELPERS
// ==========================================

/**
 * Generate unique IDs for ARIA relationships
 */
export function generateAriaId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Props for accessible labels
 */
export interface AccessibleLabelProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

/**
 * Get accessible name props (aria-label or aria-labelledby)
 */
export function getAccessibleName(
  label?: string,
  labelledBy?: string
): AccessibleLabelProps {
  if (labelledBy) {
    return { 'aria-labelledby': labelledBy };
  }
  if (label) {
    return { 'aria-label': label };
  }
  return {};
}

// ==========================================
// VISIBILITY UTILITIES
// ==========================================

/**
 * CSS class for visually hidden but screen reader accessible content
 */
export const srOnlyClass = 'sr-only';

/**
 * Props for visually hidden but accessible content
 */
export const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Get props for visually hidden elements
 * Use: <span {...getVisuallyHiddenProps()}>Screen reader text</span>
 */
export function getVisuallyHiddenProps(): { style: React.CSSProperties; className: string } {
  return {
    style: visuallyHiddenStyle,
    className: srOnlyClass
  };
}

// ==========================================
// REDUCED MOTION
// ==========================================

/**
 * Hook to detect reduced motion preference
 */
export function useReducedMotion(): boolean {
  const mediaQuery = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  return mediaQuery?.matches ?? false;
}

/**
 * Get animation duration based on reduced motion preference
 */
export function getAnimationDuration(defaultDuration: number): number {
  if (typeof window === 'undefined') return defaultDuration;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return prefersReducedMotion ? 0 : defaultDuration;
}
